import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { waUrl } from "../config/site";
import WhatsAppIcon from "./WhatsAppIcon";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", email: "", company: "", country: "", quantity: "", message: "" };

export default function QuoteMiniForm({ productType, heading = "Request a quotation for this garment", testId }) {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("product_type", productType);
      fd.append("industry", "Garment Program");
      const res = await axios.post(`${API}/leads/quote`, fd);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "quote_form_submit", reference: res.data.reference, garment: productType });
      setReference(res.data.reference);
    } catch (err) {
      toast.error("Submission failed. Please try again or reach us on WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  if (reference) {
    return (
      <div className="border border-obsidian/10 bg-white p-8 text-center" data-testid={`${testId}-success`}>
        <span className="mx-auto flex h-12 w-12 items-center justify-center bg-cleef-light">
          <Check className="h-6 w-6 text-cleef" />
        </span>
        <h3 className="mt-5 font-display text-xl font-extrabold tracking-tight">Thank you.</h3>
        <p className="mt-2 text-sm leading-relaxed text-obsidian/60">
          Your requirement has been received. Our team will review the details and contact you.
        </p>
        <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-obsidian/45">
          Reference <span className="text-cleef">{reference}</span>
        </p>
        <a
          href={waUrl(`Hello Cleef Empire, I just submitted a quotation request for ${productType}. Reference: ${reference}.`)}
          target="_blank"
          rel="noopener noreferrer"
          data-event="whatsapp_click"
          data-testid={`${testId}-success-whatsapp`}
          className="btn-dark mt-6 !bg-[#128C7E]"
        >
          <WhatsAppIcon className="h-4 w-4" /> Continue on WhatsApp
        </a>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="border border-obsidian/10 bg-white p-7 sm:p-8" data-testid={testId}>
      <h3 className="font-display text-xl font-extrabold tracking-tight">{heading}</h3>
      <p className="mt-1.5 text-sm text-obsidian/55">Two minutes of detail gets you a structured quotation.</p>
      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <label className="block">
          <span className="field-label">Full Name <span className="text-cleef">*</span></span>
          <input required className="field-input" value={form.full_name} onChange={set("full_name")} data-testid={`${testId}-name`} autoComplete="name" />
        </label>
        <label className="block">
          <span className="field-label">Business Email <span className="text-cleef">*</span></span>
          <input required type="email" className="field-input" value={form.email} onChange={set("email")} data-testid={`${testId}-email`} autoComplete="email" />
        </label>
        <label className="block">
          <span className="field-label">Company Name</span>
          <input className="field-input" value={form.company} onChange={set("company")} data-testid={`${testId}-company`} autoComplete="organization" />
        </label>
        <label className="block">
          <span className="field-label">Country <span className="text-cleef">*</span></span>
          <input required className="field-input" value={form.country} onChange={set("country")} data-testid={`${testId}-country`} autoComplete="country-name" />
        </label>
        <label className="block sm:col-span-2">
          <span className="field-label">Estimated Quantity <span className="text-cleef">*</span></span>
          <input required className="field-input" placeholder="e.g. 500 pieces" value={form.quantity} onChange={set("quantity")} data-testid={`${testId}-quantity`} />
        </label>
        <label className="block sm:col-span-2">
          <span className="field-label">Message / Specifications</span>
          <textarea rows={3} className="field-input resize-none" placeholder="Fabric, colors, sizes, branding or delivery details." value={form.message} onChange={set("message")} data-testid={`${testId}-message`} />
        </label>
      </div>
      <button type="submit" disabled={sending} data-event="quote_form_submit" data-testid={`${testId}-submit`} className="btn-primary mt-6 w-full disabled:opacity-60">
        {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Request Bulk Quotation
      </button>
    </form>
  );
}
